import { useMutation, useQueryClient } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import {
  IRoleSetup,
  NO_ACCESS_ROLE_ID,
  RoleLevel,
  SUPER_USER_ROLE_ID,
  SYNTHETIC_SELF_ID,
} from '../role.types';
import { useRoleSetupStore } from '../RoleSetup/stores';
import { useHighlightNewRoleStore } from '../stores';

import { QUERY_KEY as GET_BY_ID_QUERY_KEY } from './useGetRoleById.query';
import { GLOBAL_PERMISSIONS } from './usePermissionsList.query';
interface Params {
  isEditMode?: boolean;
  onClose?: () => void;
  onError?: () => void;
}
const OCCUPIED_NAME_TEXT = 'Details: Role with name ';
const useCreateRoleQuery = (params?: Params) => {
  const queryClient = useQueryClient();
  const setErrorFields = useRoleSetupStore((state) => state.setErrorFields);
  const setHighlightedId = useHighlightNewRoleStore((state) => state.setHighlightId);
  const { isLoading, isSuccess, mutateAsync } = useMutation(
    (payload: IRoleSetup) => {
      let grantedPermissionsIds = payload.permissions.map((permission) => _.toNumber(permission));
      if (payload.level !== RoleLevel.Elevate && payload.id === SUPER_USER_ROLE_ID) {
        grantedPermissionsIds = grantedPermissionsIds.filter((id) => !GLOBAL_PERMISSIONS.includes(id));
      }

      const currentBody = {
        name: payload.name,
        level_type: payload.level,
        description: payload.description,
        org_properties_state_config_id: payload.fieldLevelAccessId
          ? _.toNumber(payload.fieldLevelAccessId)
          : '',
        organization_id:
          payload.level !== RoleLevel.Partner ? undefined : payload.organizationId || undefined,
        granted_permission_ids: payload.id === NO_ACCESS_ROLE_ID ? [] : grantedPermissionsIds,
        role_ids_allowed_for_creation: payload?.allowedForContactsByRoleId
          ?.filter((item) => item !== SYNTHETIC_SELF_ID)
          .map((item) => _.toNumber(item)),
        self_creation_allowed: !params?.isEditMode
          ? payload?.allowedForContactsByRoleId?.includes(SYNTHETIC_SELF_ID)
          : undefined,
        allowed_for_creation_by_role_ids: payload?.isAllowedToCreateAnotherRole
          ? payload?.rolesIdsAllowedForCreation?.map((item) => _.toNumber(item))
          : [],
      };
      return params?.isEditMode
        ? api.put(API_URL.ROLES_BY_ID(payload.id || ''), currentBody)
        : api.post(API_URL.ROLES, currentBody);
    },
    {
      onSuccess: (response) => {
        const id = _.get(response, 'data.id');
        setHighlightedId(`${id || ''}`);
        if (params?.isEditMode) {
          queryClient.invalidateQueries({
            queryKey: [GET_BY_ID_QUERY_KEY],
          });
        }
      },
      onError: (response, variables) => {
        const message = _.get(response, 'response.data.elevate_error_message', '');
        if (message.includes(OCCUPIED_NAME_TEXT)) {
          if (params?.onClose) {
            params?.onClose();
          }

          setErrorFields({
            name: {
              value: variables.name,
              message: 'Role with this name already exists',
            },
          });
        } else {
          if (params?.onError) {
            params?.onError();
          }
        }
      },
    }
  );
  return {
    isLoading,
    isSuccess,
    handleSave: mutateAsync,
  };
};
export default useCreateRoleQuery;
